import { useTranslation } from 'react-i18next';
import KpiCard from './KpiCard';
import { useDashboardKpis } from './useDashboardKpis';
import { useSettings, formatNumber } from '../../context/SettingsContext';

function DollarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1v22" />
      <path d="M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6" />
    </svg>
  );
}

/**
 * Beregner sammenligningskontekst:
 * - Default: same-period (1 til i dag i nuværende mdr. vs samme antal dage i forrige mdr.)
 * - Hvis dagens dato > forrige måneds sidste dag: bruger fuld forrige måned, viser "X dage mere"
 * - Hvis i dag = sidste dag i mdr. og forrige mdr. var længere: viser "X dage mindre"
 */
function getComparisonContext(): { caption: string } {
  const today = new Date();
  const day = today.getDate();
  const lastDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const lastDayOfPrevMonth = new Date(today.getFullYear(), today.getMonth(), 0).getDate();

  // Edge A: dag overskrider forrige måneds sidste dag
  if (day > lastDayOfPrevMonth) {
    const diff = day - lastDayOfPrevMonth;
    return { caption: `denne mdr. har ${diff} dag${diff !== 1 ? 'e' : ''} mere end sidste mdr.` };
  }

  // Edge B: sidste dag i (kortere) mdr. og forrige mdr. var længere
  if (day === lastDayOfCurrentMonth && lastDayOfCurrentMonth < lastDayOfPrevMonth) {
    const diff = lastDayOfPrevMonth - lastDayOfCurrentMonth;
    return { caption: `denne mdr. har ${diff} dag${diff !== 1 ? 'e' : ''} mindre end sidste mdr.` };
  }

  // Default: same-period
  return { caption: 'vs. samme periode forrige mdr.' };
}

export default function RevenueMtdWidget() {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const { data, loading } = useDashboardKpis();

  const value = data ? formatNumber(data.revenue_mtd, settings.numberFormat, 0) : '—';
  const cur = data?.revenue_mtd ?? 0;
  // Brug same-period (klampet) som default sammenligning — ikke fuld forrige måned
  const prev = data?.revenue_prev_period ?? 0;
  const deltaPct = prev > 0 ? ((cur - prev) / prev) * 100 : null;

  const delta = deltaPct == null
    ? undefined
    : {
        label: `${deltaPct >= 0 ? '↑' : '↓'} ${Math.abs(deltaPct).toFixed(1).replace('.', ',')}%`,
        direction: (deltaPct >= 0 ? 'up' : 'down') as 'up' | 'down',
      };

  const { caption } = getComparisonContext();

  return (
    <KpiCard
      label={t('dashboard.kpi_revenue_mtd', { defaultValue: 'Omsætning · MTD' })}
      value={value}
      unit={settings.currency}
      icon={<DollarIcon />}
      iconTone="sage"
      delta={delta}
      caption={caption}
      loading={loading}
    />
  );
}
